import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { app } from "../firebase-config.js";
import { PlazaService } from "./plaza.service.js";

const db = getFirestore(app);

// ============================================================
// Campaign Category Labels & Default Plaza Message Templates
// ============================================================
const CAMPAIGN_CATEGORIES = {
    reading_reward: { label: '감상 보상', icon: 'auto_stories', color: 'green', needsSeries: true },
    attendance: { label: '출석 보상', icon: 'calendar_month', color: 'blue', needsSeries: false },
    share: { label: '공유 보상', icon: 'share', color: 'pink', needsSeries: false },
    referral: { label: '친구 초대', icon: 'group_add', color: 'orange', needsSeries: false },
    welcome: { label: '가입 환영', icon: 'waving_hand', color: 'purple', needsSeries: false }
};

// i18n Default Plaza Post Templates (10 languages)
// {title} = campaign title, {multiplier} = boost multiplier, {seriesTitle} = target series (reading only)
const PLAZA_TEMPLATES = {
    ko: {
        reading_reward: '🎉 [{title}]\n\n[{seriesTitle}] 작품 감상 보상 {multiplier}배 이벤트!\n지금 바로 정주행하고 포인트를 받아가세요! 🚀\n\n#이벤트 #감상보상',
        attendance: '🎉 [{title}]\n\n출석 보상 {multiplier}배 이벤트!\n매일 로그인하고 추가 포인트를 받아가세요! 🚀\n\n#이벤트 #출석보상',
        share: '🎉 [{title}]\n\n공유 보상 {multiplier}배 이벤트!\n작품을 공유하고 포인트를 받아가세요! 🚀\n\n#이벤트 #공유보상',
        referral: '🎉 [{title}]\n\n친구 초대 보상 {multiplier}배 이벤트!\n친구를 초대하고 더 많은 포인트를! 🚀\n\n#이벤트 #친구초대',
        welcome: '🎉 [{title}]\n\n가입 보상 {multiplier}배 이벤트!\n지금 가입하면 특별 혜택! 🚀\n\n#이벤트 #가입보상'
    },
    en: {
        reading_reward: '🎉 [{title}]\n\n{multiplier}x Reading Rewards for [{seriesTitle}]!\nBinge-read now and earn extra points! 🚀\n\n#Event #ReadingRewards',
        attendance: '🎉 [{title}]\n\n{multiplier}x Attendance Rewards!\nLog in daily for bonus points! 🚀\n\n#Event #AttendanceBoost',
        share: '🎉 [{title}]\n\n{multiplier}x Share Rewards!\nShare works and earn more! 🚀\n\n#Event #ShareBoost',
        referral: '🎉 [{title}]\n\n{multiplier}x Referral Rewards!\nInvite friends for bigger bonuses! 🚀\n\n#Event #ReferralBoost',
        welcome: '🎉 [{title}]\n\n{multiplier}x Welcome Bonus!\nSign up now for special rewards! 🚀\n\n#Event #WelcomeBoost'
    },
    ja: {
        reading_reward: '🎉 [{title}]\n\n[{seriesTitle}] 閲覧報酬{multiplier}倍イベント!\n今すぐ読んでポイントゲット! 🚀\n\n#イベント #閲覧報酬',
        attendance: '🎉 [{title}]\n\n出席報酬{multiplier}倍イベント!\n毎日ログインでボーナスポイント! 🚀\n\n#イベント #出席報酬',
        share: '🎉 [{title}]\n\n共有報酬{multiplier}倍イベント!\nシェアしてポイントゲット! 🚀\n\n#イベント #共有報酬',
        referral: '🎉 [{title}]\n\n友達招待報酬{multiplier}倍イベント!\n招待してもっとポイントを! 🚀\n\n#イベント #友達招待',
        welcome: '🎉 [{title}]\n\n加入報酬{multiplier}倍イベント!\n今すぐ登録で特別特典! 🚀\n\n#イベント #加入報酬'
    },
    zh: {
        reading_reward: '🎉 [{title}]\n\n[{seriesTitle}] 阅读奖励{multiplier}倍!\n现在阅读赚取积分! 🚀\n\n#活动 #阅读奖励',
        attendance: '🎉 [{title}]\n\n签到奖励{multiplier}倍!\n每日登录获取额外积分! 🚀\n\n#活动 #签到奖励',
        share: '🎉 [{title}]\n\n分享奖励{multiplier}倍!\n分享作品赚积分! 🚀\n\n#活动 #分享奖励',
        referral: '🎉 [{title}]\n\n邀请奖励{multiplier}倍!\n邀请好友获取更多积分! 🚀\n\n#活动 #邀请奖励',
        welcome: '🎉 [{title}]\n\n注册奖励{multiplier}倍!\n现在注册享受特别优惠! 🚀\n\n#活动 #注册奖励'
    },
    es: {
        reading_reward: '🎉 [{title}]\n\n¡Recompensa de lectura x{multiplier} para [{seriesTitle}]!\n¡Lee ahora y gana puntos extra! 🚀\n\n#Evento #RecompensaLectura',
        attendance: '🎉 [{title}]\n\n¡Recompensa de asistencia x{multiplier}!\n¡Inicia sesión diariamente! 🚀\n\n#Evento #Asistencia',
        share: '🎉 [{title}]\n\n¡Recompensa de compartir x{multiplier}! 🚀\n\n#Evento #Compartir',
        referral: '🎉 [{title}]\n\n¡Recompensa de invitación x{multiplier}! 🚀\n\n#Evento #Invitación',
        welcome: '🎉 [{title}]\n\n¡Bono de bienvenida x{multiplier}! 🚀\n\n#Evento #Bienvenida'
    },
    fr: {
        reading_reward: '🎉 [{title}]\n\nRécompense de lecture x{multiplier} pour [{seriesTitle}] !\nLisez maintenant ! 🚀\n\n#Événement #Lecture',
        attendance: '🎉 [{title}]\n\nRécompense de présence x{multiplier} !\nConnectez-vous quotidiennement ! 🚀\n\n#Événement #Présence',
        share: '🎉 [{title}]\n\nRécompense de partage x{multiplier} ! 🚀\n\n#Événement #Partage',
        referral: '🎉 [{title}]\n\nRécompense de parrainage x{multiplier} ! 🚀\n\n#Événement #Parrainage',
        welcome: '🎉 [{title}]\n\nBonus de bienvenue x{multiplier} ! 🚀\n\n#Événement #Bienvenue'
    },
    de: {
        reading_reward: '🎉 [{title}]\n\n{multiplier}x Lesebelohnung für [{seriesTitle}]!\nJetzt lesen und Punkte verdienen! 🚀\n\n#Event #Lesebelohnung',
        attendance: '🎉 [{title}]\n\n{multiplier}x Anwesenheitsbelohnung!\nTäglich einloggen! 🚀\n\n#Event #Anwesenheit',
        share: '🎉 [{title}]\n\n{multiplier}x Teilen-Belohnung! 🚀\n\n#Event #Teilen',
        referral: '🎉 [{title}]\n\n{multiplier}x Empfehlungsbelohnung! 🚀\n\n#Event #Empfehlung',
        welcome: '🎉 [{title}]\n\n{multiplier}x Willkommensbonus! 🚀\n\n#Event #Willkommen'
    },
    ru: {
        reading_reward: '🎉 [{title}]\n\nНаграда за чтение x{multiplier} для [{seriesTitle}]! 🚀\n\n#Событие #Чтение',
        attendance: '🎉 [{title}]\n\nНаграда за посещение x{multiplier}! 🚀\n\n#Событие #Посещение',
        share: '🎉 [{title}]\n\nНаграда за публикацию x{multiplier}! 🚀\n\n#Событие #Публикация',
        referral: '🎉 [{title}]\n\nРеферальная награда x{multiplier}! 🚀\n\n#Событие #Реферал',
        welcome: '🎉 [{title}]\n\nПриветственный бонус x{multiplier}! 🚀\n\n#Событие #Бонус'
    },
    th: {
        reading_reward: '🎉 [{title}]\n\nรางวัลอ่าน {multiplier} เท่า สำหรับ [{seriesTitle}]! 🚀\n\n#กิจกรรม #รางวัลอ่าน',
        attendance: '🎉 [{title}]\n\nรางวัลเข้าร่วม {multiplier} เท่า! 🚀\n\n#กิจกรรม #เข้าร่วม',
        share: '🎉 [{title}]\n\nรางวัลแชร์ {multiplier} เท่า! 🚀\n\n#กิจกรรม #แชร์',
        referral: '🎉 [{title}]\n\nรางวัลชวนเพื่อน {multiplier} เท่า! 🚀\n\n#กิจกรรม #ชวนเพื่อน',
        welcome: '🎉 [{title}]\n\nโบนัสต้อนรับ {multiplier} เท่า! 🚀\n\n#กิจกรรม #ต้อนรับ'
    },
    vi: {
        reading_reward: '🎉 [{title}]\n\nThưởng đọc x{multiplier} cho [{seriesTitle}]! 🚀\n\n#SuKien #ThuongDoc',
        attendance: '🎉 [{title}]\n\nThưởng điểm danh x{multiplier}! 🚀\n\n#SuKien #DiemDanh',
        share: '🎉 [{title}]\n\nThưởng chia sẻ x{multiplier}! 🚀\n\n#SuKien #ChiaSe',
        referral: '🎉 [{title}]\n\nThưởng giới thiệu x{multiplier}! 🚀\n\n#SuKien #GioiThieu',
        welcome: '🎉 [{title}]\n\nThưởng chào mừng x{multiplier}! 🚀\n\n#SuKien #ChaoMung'
    }
};

/**
 * Get default Plaza post content for a campaign
 */
function getDefaultPostContent(eventData, lang = 'ko') {
    const templates = PLAZA_TEMPLATES[lang] || PLAZA_TEMPLATES['ko'];
    const category = eventData.boostCategory || 'reading_reward';
    let template = templates[category] || templates['reading_reward'];

    return template
        .replace(/\{title\}/g, eventData.title || '')
        .replace(/\{multiplier\}/g, eventData.multiplier || 2)
        .replace(/\{seriesTitle\}/g, eventData.seriesTitle || '');
}

export { CAMPAIGN_CATEGORIES, PLAZA_TEMPLATES, getDefaultPostContent };

export const AdminMarketingService = {
    /**
     * Fetch all marketing events
     * [FIX] Changed from 'events' to 'marketing_events' to match server-side query
     */
    async fetchEvents() {
        const snapshot = await getDocs(query(collection(db, 'marketing_events'), orderBy('createdAt', 'desc')));
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    },

    /**
     * Create a campaign event
     * Supports: reading_reward, attendance, share, referral, welcome
     * @param {Object} eventData
     */
    async createEvent(eventData) {
        // Validation
        const category = eventData.boostCategory || 'reading_reward';
        const catConfig = CAMPAIGN_CATEGORIES[category];

        if (!eventData.title) {
            throw new Error("캠페인 제목을 입력해주세요.");
        }
        if (catConfig?.needsSeries && !eventData.seriesId) {
            throw new Error("감상 보상 캠페인은 대상 작품을 선택해야 합니다.");
        }

        const newEventId = `event_${Date.now()}`;

        // 1. Create Event Document in marketing_events (matches server-side)
        await setDoc(doc(db, "marketing_events", newEventId), {
            ...eventData,
            boostCategory: category,
            startDate: new Date(eventData.startDate),
            endDate: new Date(eventData.endDate),
            createdAt: new Date(),
            isActive: true
        });

        // 2. Auto-Post to Plaza (if enabled)
        if (eventData.autoPost !== false) {
            try {
                // Use custom message if provided, otherwise use i18n default
                const postContent = eventData.customPostMessage || getDefaultPostContent(eventData, 'ko');

                const tags = ['이벤트', catConfig?.label?.replace(/\s/g, '') || category];
                if (eventData.seriesTitle) {
                    const safeTitle = eventData.seriesTitle.replace(/[[\]()\s]/g, '_');
                    tags.push(safeTitle);
                }
                if (eventData.variant) tags.push(eventData.variant);

                await PlazaService.createPost({
                    authorId: 'system',
                    authorName: 'SsulwhaWorld',
                    authorPhoto: null,
                    content: postContent,
                    hashtags: tags,
                    imageUrls: [],
                    isAdminContent: true,
                    type: 'text'
                });
            } catch (e) {
                console.error("Failed to auto-post to Plaza:", e);
            }
        }

        return newEventId;
    },

    /**
     * Toggle event active status
     * [FIX] Changed from 'events' to 'marketing_events'
     */
    async toggleEventStatus(eventId, currentStatus) {
        await updateDoc(doc(db, "marketing_events", eventId), {
            isActive: !currentStatus
        });
        return !currentStatus;
    },

    /**
     * Delete an event
     * [FIX] Changed from 'events' to 'marketing_events'
     */
    async deleteEvent(eventId) {
        await deleteDoc(doc(db, "marketing_events", eventId));
    }
};
